import { PRACTICE_TYPE_ENUM } from 'data/enums'

export const buildWelcomeMessage = (candidate) => {
  const fallback = "Search for jobs by describing what you're looking for — specialty, location, salary, benefits, or any combination."

  if (!candidate) return fallback

  const bullets = []

  const candidateTypeName = candidate.candidateType?.name
  const specialties = (candidate.specialties || []).map(s => s.name).filter(Boolean)
  const subspecialties = (candidate.subspecialties || []).map(s => s.name).filter(Boolean)

  if (candidateTypeName && specialties.length > 0) {
    const specialtyText = specialties.slice(0, 2).join(' and ')
    bullets.push(`${candidateTypeName} specializing in ${specialtyText}`)
  } else if (candidateTypeName) {
    bullets.push(candidateTypeName)
  } else if (specialties.length > 0) {
    bullets.push(`Specializing in ${specialties.slice(0, 2).join(' and ')}`)
  }

  if (subspecialties.length > 0) {
    bullets.push(`Subspecialty: ${subspecialties.slice(0, 2).join(', ')}`)
  }

  const preferredCity = candidate.preferredCity?.trim()
  const preferredStates = (candidate.preferredStates || []).filter(Boolean)
  if (preferredCity && preferredStates.length > 0) {
    bullets.push(`Looking in ${preferredCity}, ${preferredStates.join(', ')}`)
  } else if (preferredCity) {
    bullets.push(`Looking in ${preferredCity}`)
  } else if (preferredStates.length > 0) {
    bullets.push(`Looking in ${preferredStates.join(', ')}`)
  }

  const practiceTypes = (candidate.preferredPracticeTypes || [])
    .map(pt => PRACTICE_TYPE_ENUM[pt] || pt)
    .filter(Boolean)
  if (practiceTypes.length > 0) {
    bullets.push(`Prefer ${practiceTypes.join(', ')} positions`)
  }

  const licensedStates = (candidate.licensedStates || []).filter(Boolean)
  const licensedDiffers = licensedStates.length > 0
    && licensedStates.some(ls => !preferredStates.includes(ls))
  if (licensedDiffers) {
    bullets.push(`Licensed in ${licensedStates.join(', ')}`)
  }

  const visas = (candidate.visas || []).map(v => v.name || v).filter(Boolean)
  if (visas.length > 0) {
    bullets.push(`Visa: ${visas.join(', ')}`)
  }

  if (bullets.length === 0) return fallback

  if (bullets.length === 1) {
    return `I see you're a ${bullets[0]}. Tell me what you're looking for — or just say "show me jobs" to browse the latest postings.`
  }

  const bulletList = bullets.map(b => `- ${b}`).join('\n')
  return `Here's what I know about your preferences:\n${bulletList}\n\nI'll use this to rank how well jobs match your profile. What are you looking for?`
}

export const buildTypingIndicatorMessages = ({ candidate, jobListingArguments, lastUserMessage }) => {
  const searchSpecific = []
  const actionOriented = [
    'Scanning our database of open positions...',
    'Evaluating job details against your preferences...',
    'Ranking the best matches for you...',
  ]
  const fun = [
    'Scrubbing in...',
    'Consulting the charts...',
    'Prepping your results...',
  ]

  const specialty = jobListingArguments?.specialties?.[0] || candidate?.specialties?.[0]?.name
  const location = jobListingArguments?.locations?.[0]?.location
  const practiceType = jobListingArguments?.practiceTypes?.[0]
  const message = lastUserMessage?.toLowerCase() || ''

  const salaryKeywords = ['salary', 'pay', 'compensation', '$', 'income', 'earning']
  const benefitKeywords = ['loan', 'visa', 'pslf', 'forgiveness', 'benefit', 'relocation', 'signing bonus', 'sign-on']

  if (specialty) searchSpecific.push(`Searching our job board for ${specialty} positions...`)
  if (location) searchSpecific.push(`Looking for openings in ${location}...`)
  if (practiceType) searchSpecific.push(`Filtering for ${practiceType} roles...`)

  if (salaryKeywords.some(keyword => message.includes(keyword))) {
    searchSpecific.push('Checking salary ranges for your criteria...')
  }

  if (benefitKeywords.some(keyword => message.includes(keyword))) {
    const matched = benefitKeywords.find(keyword => message.includes(keyword))
    searchSpecific.push(`Checking ${matched} eligibility...`)
  }

  return [...searchSpecific, ...actionOriented, ...fun].slice(0, 8)
}

export const formatSalary = (salary, currency) => {
  if (!salary || salary <= 1) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  }).format(salary)
}

export const getPracticeTypeString = value => {
  const practiceTypeMap = {
    1: 'FullTime',
    2: 'Locum',
    3: 'Telemedicine',
    4: 'PartTime',
    5: 'PRN',
    6: 'Travel',
  }
  const stringKey = practiceTypeMap[value]

  return PRACTICE_TYPE_ENUM[stringKey] || null
}

/**
 * Generates 3 personalized example queries based on candidate profile.
 * Falls back to generic queries if profile data is missing.
 *
 * @param {Object} candidate - Redux candidate state
 * @returns {Array<string>} Array of exactly 3 query strings
 */
export const generateExampleQueries = (candidate) => {
  const DEFAULT_QUERIES = [
    'Emergency medicine in California',
    'Cardiology with loan forgiveness',
    'Part-time near Boston',
  ]

  if (!candidate) return DEFAULT_QUERIES

  const queries = []

  const getLocationString = (city, state) => {
    const c = city?.trim()
    const s = state?.trim()
    if (c && s) return `near ${c}, ${s}`
    if (c) return `near ${c}`
    if (s) return `in ${s}`
    return null
  }

  const specialty = candidate.specialties?.[0]?.name
  const subspecialty = candidate.subspecialties?.[0]?.name
  const preferredCity = candidate.preferredCity
  const preferredState = candidate.preferredStates?.[0]
  const currentState = candidate.state
  const currentCity = candidate.city
  const practiceTypes = candidate.preferredPracticeTypes || []

  if (specialty && (preferredState || preferredCity)) {
    const location = getLocationString(preferredCity, preferredState)
    queries.push(`${specialty} ${location}`)
  } else if (specialty) {
    queries.push(`${specialty} positions`)
  } else if (preferredState || preferredCity) {
    const location = getLocationString(preferredCity, preferredState)
    queries.push(`Jobs ${location}`)
  } else if (currentState || currentCity) {
    const location = getLocationString(currentCity, currentState)
    queries.push(`Jobs ${location}`)
  } else {
    queries.push(DEFAULT_QUERIES[0])
  }

  if (subspecialty && (preferredCity || currentCity)) {
    const city = preferredCity || currentCity
    queries.push(`${subspecialty} near ${city}`)
  } else if (practiceTypes.length > 0) {
    const practiceType = PRACTICE_TYPE_ENUM[practiceTypes[0]] || practiceTypes[0]
    const location = preferredState || currentState

    if (specialty) {
      queries.push(`${practiceType} ${specialty}`)
    } else if (location) {
      queries.push(`${practiceType} positions in ${location}`)
    } else {
      queries.push(`${practiceType} positions`)
    }
  } else if (specialty) {
    queries.push(`${specialty} with loan forgiveness`)
  } else {
    queries.push(DEFAULT_QUERIES[1])
  }

  const usedLoanForgiveness = queries.some(q => q.includes('loan forgiveness'))

  if (specialty && !usedLoanForgiveness) {
    queries.push(`${specialty} with loan forgiveness`)
  } else if (practiceTypes.length > 1) {
    const practiceType = PRACTICE_TYPE_ENUM[practiceTypes[1]] || practiceTypes[1]
    queries.push(`${practiceType} positions`)
  } else if (currentState && currentState !== preferredState) {
    const location = getLocationString(currentCity, currentState)
    queries.push(`Jobs ${location}`)
  } else {
    queries.push(DEFAULT_QUERIES[2])
  }

  const unique = [...new Set(queries)]

  for (const dq of DEFAULT_QUERIES) {
    if (unique.length >= 3) break
    if (!unique.includes(dq)) unique.push(dq)
  }

  return unique.slice(0, 3)
}

export const generateFollowUpSuggestions = ({ rankedJobs, jobListingArguments }) => {
  const suggestions = []
  const hasResults = rankedJobs?.length > 0
  const specialty = jobListingArguments?.specialties?.[0]

  if (!hasResults) {
    if (specialty) {
      suggestions.push(`Show all ${specialty} jobs`)
    }

    suggestions.push('Try a broader location')
    suggestions.push('Show me the latest jobs')

    return suggestions.slice(0, 3)
  }

  const locations = rankedJobs
    .map(rj => rj.job?.state || rj.job?.city)
    .filter(Boolean)
  const uniqueLocations = [...new Set(locations)]

  const hospitals = rankedJobs
    .map(rj => rj.job?.hospitalName || rj.job?.employer?.name)
    .filter(Boolean)
  const uniqueHospitals = [...new Set(hospitals)]

  if (uniqueLocations.length > 0) {
    const location = uniqueLocations[0]
    suggestions.push(`What's the cost of living in ${location}?`)
  }

  if (uniqueHospitals.length > 0) {
    const hospital = uniqueHospitals[0]
    const shortName = hospital.length > 30 ? hospital.substring(0, 27) + '...' : hospital
    suggestions.push(`Tell me about ${shortName}`)
  }

  if (suggestions.length < 3) {
    suggestions.push('Which of these are near good school districts?')
  }

  if (suggestions.length < 3) {
    suggestions.push('Which have sign-on bonuses?')
  }

  if (specialty && suggestions.length < 3) {
    suggestions.push(`What's the job market like for ${specialty}?`)
  }

  if (uniqueLocations.length > 1 && suggestions.length < 3) {
    suggestions.push('Compare cost of living between these locations')
  }

  return suggestions.slice(0, 3)
}
